// POSSIBLE CONNECTIONS inside an Ongoing (busy-my-web#420, Figma section 40011046-161445).
//
// The section reads a story's pending suggestions as a TABLE rather than as a stack of cards:
//
//   Name (module glyph + title) · Possible connection (why) · Last update · Confidence · View ·
//   Not related · Connect
//
// and gives each candidate a review view of its own — the thing itself in the middle, a "Why Busy
// found this" card listing the reasons, and a Connect / Not related chooser — before the answer is
// given. Answering removes the row, and a green notice says what happened with an Undo beside it.
//
// WHAT IS NEW HERE AND WHAT IS NOT.
//
// The offers are the same Suggested Contains→Timeline edges the app has always had, read through
// lib/possibleMatches.js's grouping of `ongoing/suggestions`, and the two answers are still the
// store's `addSuggestedThread` / `rejectSuggestedThread`. Nothing about the DATA changes. What this
// module adds is the reading the new frames need and the old cards never had:
//
//   · a CONFIDENCE BAND — Strong / Likely / Possible — off the `confidence` score the suggestion
//     payload already carries (TimelineSrv's OngoingSuggestionResponse.Confidence, 0–1);
//   · the "Why Busy found this" REASONS as a list, assembled from the server's one-sentence
//     `reason` plus the signal names behind it (`signals`), because the frame prints three lines and
//     the payload has never carried a list;
//   · the notice wording, and the module a row's glyph is drawn from.
//
// WHAT THE PAYLOAD CARRIES NOW (Pouria, 2026-10-01, frame 40011046:161537): since
// busy-timeline-srv#42 the list holds `kind: 'entity'` offers beside the thread ones — a task, a
// note, an event, a research pack — each with its `module`, a `band`, a `reasons` list and a
// `facts` object, answered on the same relationship edge. The table reads them all (lib/
// possibleMatches' `ongoingConnectionCards`), draws each in its module's glyph and colour, dates
// them the frame's way ("Sep 7") and lists them newest first.
//
// WHAT IT STILL DOES NOT CARRY (listed on the issue, drawn conservatively here):
//   · a REASON LIST on a THREAD offer — one `Reason` sentence and machine-named `Signals` is all there is, so the
//     three tidy sentences in the frame are approximated by the sentence plus the signals it was
//     built from, spelled as words.
//   · no confidence at all on a classifier suggestion (`Confidence` is nullable, and null on the
//     classifier's own). Those rows read "Possible", the honest bottom rung — never "Strong" by
//     the accident of `Number(null) === 0` or a made-up score.
//
// Pure — no store, no api, nothing mounted.

import { moduleColor } from '@/lib/moduleColors'
import { moduleIcon, moduleIconKey } from '@/lib/moduleIcons'
import { countLabel, countText } from '@/lib/briefings/rows'

export const TEXT = {
  heading: 'Possible connections',
  // The box's prompt while the table has the middle (busy-my-web#539, frame 40011046:161537).
  listPlaceholder: 'Ask about these possible connections...',
  // The column headings, in the frame's order.
  name: 'Name',
  reason: 'Possible connection',
  lastUpdate: 'Last update',
  confidence: 'Confidence',
  // The three answers per row.
  view: 'View',
  notRelated: 'Not related',
  connect: 'Connect',
  // …and what "View" becomes on the row whose card is open (busy-my-web#539, frame
  // 40011806:236185): a plain, unclickable label — the row already answers "which one is this?",
  // pressing it again would only reopen what is already open.
  viewing: 'Viewing',
  // The review CARD (busy-my-web#539 restyle of #420/#504's strip into a self-contained card,
  // frames 40011765:160397 / 40011806:236185).
  why: 'Why this might connect?',
  close: 'Close',
  // The Summary / Original Email toggle (frame 40011806:236185).
  summaryTab: 'Email Summary',
  originalTab: 'Original Email',
  showOriginal: 'Show original Email',
  showSummary: 'Show Summary',
  to: 'To',
  helpful: 'Was this helpful?',
  // Neither an AI summary nor the raw email body is on the suggestion payload today (checked
  // against TimelineSrv's OngoingSuggestionResponse — see the review card's own note); these say so
  // rather than guessing at either one.
  noSummary: 'No summary is available for this email yet.',
  noOriginal: 'The original email text isn’t available here yet.',
  // The box's placeholder while a candidate is under review (frame 40011046:161598).
  askPlaceholder: 'Ask about this email...',
  connecting: 'Connecting',
  undo: 'Undo',
  // The empty state — reachable the moment the last row is answered, and a blank middle reads as a
  // fault rather than as a finished job.
  none: 'Nothing is waiting to be connected to this Ongoing.',
}

/**
 * The confidence bands, strongest first, with the score at which each one starts.
 *
 * Three rungs because the frame draws three ("Strong", "Likely", "Possible"), and the thresholds
 * are the obvious thirds of a 0–1 score: there is no server-side band to mirror and no recorded
 * number to honour, so the split is stated here in one place where a real one can replace it.
 *
 * The palette is the frame's, sampled off it: Strong is the product indigo FILLED (this is the one
 * the eye should land on), Likely the same indigo on the palest blue plate, Possible the neutral
 * grey plate every "no action needed" state in this app already uses. Deliberately not a traffic
 * light — a weak match is a guess, not a problem (the same reasoning lib/evidence.js's
 * `strengthBadge` records for the popover's badges).
 */
export const CONFIDENCE_BANDS = [
  { key: 'strong', label: 'Strong', from: 0.8, class: 'bg-[#3741f3] text-white' },
  { key: 'likely', label: 'Likely', from: 0.5, class: 'bg-[#f4f9ff] text-[#3741f3]' },
  { key: 'possible', label: 'Possible', from: 0, class: 'bg-[#e5e7eb] text-[#4b5563]' },
]

/** The bottom rung — what an unscored suggestion reads as. */
export const UNSCORED_BAND = CONFIDENCE_BANDS[CONFIDENCE_BANDS.length - 1]

/**
 * The band a score falls in.
 *
 * A MISSING score is not a zero and it is not a strong match either: the classifier's own
 * suggestions carry no `Confidence` at all, and they read "Possible" — the honest bottom rung —
 * rather than borrowing a number nobody produced.
 *
 * @param {number|null|undefined} confidence 0–1, or null/undefined when the source gave no score
 * @returns {{key: string, label: string, class: string}}
 */
export function confidenceBand(confidence) {
  if (confidence === null || confidence === undefined || confidence === '') return UNSCORED_BAND
  const n = Number(confidence)
  if (!Number.isFinite(n)) return UNSCORED_BAND
  return CONFIDENCE_BANDS.find(b => n >= b.from) || UNSCORED_BAND
}

/**
 * The band of a whole CARD — the strongest offer behind it.
 *
 * A card can group several edges (lib/possibleMatches.js groups by sender + Ongoing) and the row
 * asks one question about all of them, so the number it shows is the best evidence it has. Taking
 * the weakest, or an average, would understate a card whose strongest edge is exactly the reason it
 * is being offered at all.
 */
export function cardConfidence(card) {
  // The server's own BAND wins where it sent one (busy-timeline-srv#42's `band`: strong / likely /
  // possible — Pouria, 2026-10-01, frame 40011046:161537): it is the service's reading of its own
  // score, and the thirds below are only this module's stand-in for it. The strongest band behind
  // the card, for the reason the scores take their maximum.
  const bands = (card?.offers || [])
    .map(o => CONFIDENCE_BANDS.findIndex(b => b.key === String(o?.band || '').toLowerCase()))
    .filter(i => i >= 0)
  if (bands.length) return CONFIDENCE_BANDS[Math.min(...bands)]
  const scores = (card?.offers || [])
    .map(o => o?.confidence)
    .filter(c => c !== null && c !== undefined && c !== '' && Number.isFinite(Number(c)))
    .map(Number)
  if (!scores.length) return UNSCORED_BAND
  return confidenceBand(Math.max(...scores))
}

/**
 * The "Possible connection" cell: WHY, in as few words as the column has room for.
 *
 * The server's own sentence where it wrote one ("mentions 'tiles', which Kitchen renovation is
 * researching"), else the signal names spelled as words, else a plain statement that the sender is
 * followed — never an empty cell, because a row with no reason at all is a question the user cannot
 * answer.
 */
export function connectionReason(card) {
  const head = (card?.offers || [])[0] || {}
  const reason = String(head.reason || '').trim()
  if (reason) return reason
  const signals = String(head.signals || '').trim()
  if (signals) return signals
  const story = String(head.story || card?.category || '').trim()
  return story ? `May belong to ${story}` : 'Related to this Ongoing'
}

/**
 * The reasons the review view lists under "Why Busy found this".
 *
 * The frame prints three tidy sentences and the payload carries one sentence plus a list of machine
 * signal names, so the list is the sentence FIRST (it is the only prose there is) and then each
 * signal, spelled as words and capitalised, minus any that merely repeats the sentence. Deduped and
 * order-stable: the server ranks its signals, and re-sorting them here would be this module
 * inventing an opinion.
 *
 * Empty for a candidate with nothing recorded, so the card can be left off rather than drawn with a
 * heading over nothing.
 */
export function whyLines(card) {
  const out = []
  const seen = new Set()
  const push = (line) => {
    const text = String(line || '').trim()
    if (!text) return
    const key = text.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    out.push(text)
  }
  for (const offer of (card?.offers || [])) {
    push(offer?.reason)
    // …and an entity offer's own reason LIST (busy-timeline-srv#42), which is the prose the frame
    // prints; the one-sentence `reason` is usually its first line, and the dedupe drops the repeat.
    for (const line of (offer?.reasons || [])) push(line)
    for (const signal of String(offer?.signals || '').split('·')) push(sentenceCase(signal))
  }
  return out
}

/** "shared participants" -> "Shared participants". The signals arrive machine-named. */
function sentenceCase(text) {
  const s = String(text || '').trim().replace(/[-_]+/g, ' ')
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''
}

/**
 * WHICH MODULE a candidate is, for its glyph and its colour.
 *
 * Every suggestion the service offers today is a thread, i.e. an email, so that is the honest
 * default. The field is read first all the same (`module` / `kind`), so the day busy-timeline-srv
 * offers a task or a contact the row draws it correctly without another change here — and anything
 * it does not recognise falls back to the email glyph rather than to a blank cell.
 */
// A DOCUMENT is not one of the seven modules the glyph map knows (busy-my-web#710: the knowledge
// graph offers files to an Ongoing, and the BFF names that row's module `document`). Read before the
// module map, so a file is drawn as a file rather than falling back to the email envelope.
const DOCUMENT_MODULES = new Set(['document', 'documents', 'file', 'files', 'pdf'])

export function connectionModule(card) {
  const head = (card?.offers || [])[0] || {}
  const raw = String(card?.module || head.module || '').trim().toLowerCase()
  if (DOCUMENT_MODULES.has(raw)) return 'document'
  // "thread" is the suggestion KIND, not a module — it means an email.
  const key = !raw || raw === 'thread' ? 'email' : raw
  // `moduleIconKey` is the one that answers "is this a module at all" — `moduleIcon` falls back to
  // the globe for anything unknown, so asking IT would draw a research pack for a typo.
  return moduleIconKey(key) || 'email'
}

/**
 * THE NAME COLUMN — the candidate's own title, not its sender.
 *
 * The frame's Name column reads "Buy Lens cleaner", "Installment payment", "Summer Review": each
 * row names the THING, because the question is whether that thing belongs here. The card's own
 * `name` is the SENDER, which is right where it came from — a workspace card groups a person's mail
 * and asks "do these belong somewhere?" — and wrong here.
 *
 * The exception is a card that groups SEVERAL emails: they have several subjects and only one
 * sender, so the sender is then the only honest one-line name for the question being asked.
 */
export function connectionName(card) {
  const offers = card?.offers || []
  const subject = offers.length === 1 ? String(offers[0]?.subject || '').trim() : ''
  return subject || String(card?.name || '').trim() || 'This email'
}

/**
 * THE "LAST UPDATE" CELL — the frame's own date: "Sep 7", "Aug 22" (Pouria, 2026-10-01, frame
 * 40011046:161537).
 *
 * Not lib/emailSession's `lastUpdate` ("7 Sept", en-GB): that is the mailbox's reading and stays
 * everywhere the mailbox is drawn. This table is its own frame, and the frame writes month first.
 * A date from another year says which one, so "Sep 7" never stands for a September long gone.
 * '' for no date, so the cell is simply empty rather than "Invalid Date".
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function connectionDate(value, now = new Date()) {
  const d = value ? new Date(value) : null
  if (!d || Number.isNaN(d.getTime())) return ''
  const day = `${MONTHS[d.getMonth()]} ${d.getDate()}`
  return d.getFullYear() === now.getFullYear() ? day : `${day}, ${d.getFullYear()}`
}

/**
 * A module's glyph and colour — the module map's, plus the DOCUMENT the map does not have (the
 * pack's `file` glyph in the contact grey, the same neutral lib/graphConnections draws a document
 * node in).
 */
export const DOCUMENT_GLYPH = Object.freeze({ icon: 'file', color: '#4B5563' })

export function moduleGlyph(module) {
  if (module === 'document') return { ...DOCUMENT_GLYPH }
  return { icon: moduleIcon(module), color: moduleColor(module) }
}

/**
 * The confidence chip of THIS journey (Figma 40011046:161445, measured on frame 40011812:239492):
 * "Strong" is the soft indigo plate (#eef0fe / #3741f3) in the table and in the view card, where
 * the chat's table fills it; Likely and Possible keep the colours the table already uses. Only the
 * class changes — the key and the label stay the band's.
 */
export const SOFT_STRONG_CLASS = 'bg-[#eef0fe] text-[#3741f3]'

export function journeyBand(band) {
  if (!band) return UNSCORED_BAND
  return band.key === 'strong' ? { ...band, class: SOFT_STRONG_CLASS } : band
}

/** A row's newest date as a number, 0 when it has none — the sort key below. */
const timeOf = (value) => {
  const t = value ? new Date(value).getTime() : NaN
  return Number.isNaN(t) ? 0 : t
}

/**
 * One TABLE ROW, from one of lib/possibleMatches.js's cards.
 *
 * Deliberately a projection of the existing card rather than a second reading of the raw
 * suggestions: the story's briefing count, the old cards and this table then cannot disagree about
 * what is pending, and an edge answered anywhere is answered everywhere.
 */
export function connectionRow(card, now = new Date()) {
  const module = connectionModule(card)
  const head = (card?.offers || [])[0] || {}
  const glyph = moduleGlyph(module)
  return {
    id: card?.id || '',
    card,
    name: connectionName(card),
    module,
    icon: glyph.icon,
    color: glyph.color,
    // The edges the row's two answers are given on — flattened here so the view never has to reach
    // back through the card for them (and never has to guard a shape this function guarantees).
    relationshipIds: (card?.relationshipIds || []).filter(Boolean),
    reason: connectionReason(card),
    // The frame's "Sep 7" off the card's newest date; the card's own wording only where no raw
    // date came with it (a card built by hand, or an older payload).
    lastUpdate: connectionDate(card?.received, now) || String(card?.updated || '').trim(),
    updatedAt: card?.received || null,
    // Is this candidate MAIL? Decides what its review card can offer (a Summary / Original Email
    // toggle and a sender line only mean something for an email) and what "View" opens.
    isEmail: module === 'email',
    // The entity behind a non-email row (busy-timeline-srv#42), for its review card's "Open".
    entityId: head.entityId || null,
    confidence: journeyBand(cardConfidence(card)),
    why: whyLines(card),
    // The graph node's own summary (busy-my-web#710) — the view card's Summary mode. '' when the
    // node has not been summarised (the card then opens on the original).
    summary: String(head.summary || '').trim(),
    // The sentence of facts the frames put over the Why card (busy-my-web#495 (a)).
    facts: connectionFacts(card),
    // The story the chooser is offering it TO — the review card's header chip (busy-my-web#539,
    // frame 40011765:160397). `matchCards`' own `category` is this same title (lib/possibleMatches.js
    // sets it from the offer's `story`); read here rather than re-derived, so the chip and the
    // grouping this row came from can never name two different Ongoings.
    story: String(card?.category || '').trim(),
    // The sender line the review card's header/body draw (busy-my-web#539): the RAW header
    // ("Prada <noreply@prada.com>"), not the parsed display name `card.name`/`name` above already
    // are — a person reading the review wants to see exactly what the mail arrived as.
    sender: String((card?.offers || [])[0]?.sender || '').trim(),
    received: (card?.offers || [])[0]?.received || null,
  }
}

/**
 * The table, NEWEST FIRST (Pouria, 2026-10-01, frame 40011046:161537 lists Sep 7, Sep 4, Sep 2,
 * Aug 31, Aug 22). The server ranks the mail first and then the entities by score, which is a
 * ranking for deciding what to OFFER, not an order to read a list of dated rows in. Stable, so
 * rows with the same (or no) date keep the server's order between them; undated rows go last.
 */
export function connectionRows(cards, now = new Date()) {
  return (Array.isArray(cards) ? cards : [])
    .filter(Boolean)
    .map((card, i) => ({ row: connectionRow(card, now), i }))
    .sort((a, b) => (timeOf(b.row.updatedAt) - timeOf(a.row.updatedAt)) || (a.i - b.i))
    .map(x => x.row)
}

/**
 * "5 Possible connections" — the Briefing row, the crumb chip and the section's own count (Pouria,
 * 2026-10-01, frame 40011046:161537's crumb reads "5 Possible conne…"). One is "1 Possible
 * connection". '' for none.
 */
export function connectionsLabel(count) {
  const n = Number(count) || 0
  if (n <= 0) return ''
  return n === 1 ? `1 ${TEXT.heading.replace(/s$/, '')}` : `${countText(n)} ${TEXT.heading}`
}

/**
 * THE BRIEFING'S PER-KIND ROWS while the table is open (Pouria, 2026-10-01, frame
 * 40011046:161537): under the crumb "… › 5 Possible connections ✕" the strip lists what the five
 * ARE — "1 Email", "1 Task", "1 Note", "1 Event", "1 Research pack" — each with a "View ↗".
 *
 * One entry per module that has rows, in the frame's order (mail first, then the app's modules in
 * the order the story briefing's own rows list them), counted off the table's own rows so the
 * numbers are the table's. The frame's copy mixes "1 task" with "1 Note" and writes "1 Research
 * packs" for one; the nouns here are the notice's own (`MODULE_NOUN`), capitalised as most of the
 * frame's are and plural only when there is more than one.
 */
const KIND_ORDER = ['email', 'task', 'note', 'event', 'research', 'document', 'contact', 'messaging']

export function connectionKinds(rows) {
  const counts = new Map()
  for (const row of (Array.isArray(rows) ? rows : [])) {
    if (!row) continue
    const module = connectionModule({ module: row.module })
    counts.set(module, (counts.get(module) || 0) + 1)
  }
  return KIND_ORDER.filter(m => counts.has(m)).map((module) => {
    const n = counts.get(module)
    const noun = MODULE_NOUN[module]
    const glyph = moduleGlyph(module)
    return {
      module,
      count: n,
      // The shared Briefing count (busy-my-web#609): "1,204 Emails".
      text: countLabel(n, noun),
      icon: glyph.icon,
      color: glyph.color,
    }
  })
}

/**
 * Keep only one module's rows — what a kind row's "View ↗" narrows the table to. No module (or
 * one the table has no rows of) is the whole table, so a stale choice can never empty it.
 */
export function rowsOfModule(rows, module) {
  const list = Array.isArray(rows) ? rows : []
  const key = String(module || '').trim()
  if (!key) return list
  const kept = list.filter(r => r && r.module === key)
  return kept.length ? kept : list
}

/**
 * What OPENING a non-email candidate means, from its review card (Pouria, 2026-10-01): the task,
 * note or event opens IN THE BOX (the `smartbox-edit-entity` event every other opener uses), and a
 * research pack opens as a pack of the Ongoing it lives in (its `facts.homeOngoingId`, else the
 * story being read). Null for mail (the table's own "View" reads it in the middle) and for a row
 * with no entity id to open.
 */
const MENU_OF = { task: 'tasks', note: 'notes', event: 'events', contact: 'contacts' }

export function connectionOpenTarget(row, ongoingId = '') {
  if (!row || row.isEmail) return null
  const id = String(row.entityId || '').trim()
  if (!id) return null
  // A FILE opens as the file's own Briefing card (`fileBriefing/open`, what the graph panel and the
  // file explorer open a document with — busy-my-web#710).
  if (row.module === 'document') return { kind: 'document', file: { id, name: String(row.name || '') } }
  if (row.module === 'research') {
    const facts = row.card?.facts || (row.card?.offers || [])[0]?.facts || {}
    const home = String(facts.homeOngoingId || ongoingId || '').trim()
    return home ? { kind: 'route', path: `/ongoing/${home}?pack=${encodeURIComponent(id)}` } : null
  }
  const menu = MENU_OF[row.module]
  if (!menu) return null
  return { kind: 'entity', detail: { menu, id, title: String(row.name || ''), ongoingId: String(ongoingId || '') || null } }
}

/** "Task", "Research pack" — what a module is CALLED, for the review card's facts line. */
export function moduleNounOf(module) {
  return MODULE_NOUN[connectionModule({ module })] || MODULE_NOUN.email
}

/**
 * The box's prompt while a candidate is reviewed: "Ask about this email…" (frame 40011046:161598)
 * — and "Ask about this task…", "…this research pack…" for the other modules the table now lists.
 */
export function askLabel(module) {
  return `Ask about this ${moduleNounOf(module).toLowerCase()}…`
}

/** "Open task" / "Open research pack" — the review card's own link for a non-email candidate. */
export function openLabel(module) {
  return `Open ${moduleNounOf(module).toLowerCase()}`
}

/**
 * The card's own loading row while a commit is out (busy-my-web#539, frame 40011810:237957):
 * "Connecting Installment payment to Buy sunglasses…" — the frame's exact wording pattern, replacing
 * the old strip's bare "Connecting…" now that the card can name both sides of the edge.
 */
export function connectingLabel(name, story) {
  const who = String(name || '').trim()
  const where = String(story || '').trim()
  if (who && where) return `${TEXT.connecting} ${who} to ${where}…`
  if (who) return `${TEXT.connecting} ${who}…`
  return `${TEXT.connecting}…`
}

/**
 * The green notice after a connect: "Installment payment · Email connected to this ongoing".
 *
 * The module is named because a story gains more than mail: after a task or a contact is connected,
 * "connected to this ongoing" alone would not say WHAT was. The separator is the app's middle dot,
 * not the frame's full stop — the frame's own crumb and chip lines use the dot everywhere else, and
 * a full stop mid-sentence reads as a typo.
 */
/**
 * THE FACTS LINE above the Why card (2026-09-15 parity audit F1-7, busy-my-web#495 (a); frames
 * 40011046:161598 / 40011151:211985).
 *
 * The frames put a plain sentence of FACTS over the reasons — who it is from, when it last moved,
 * how many mails the card stands for — so the question can be answered without opening the thing.
 * The offers already carry every part of it (and, since busy-timeline-srv#42, an entity row carries
 * a `facts` object as well); this joins the parts that exist with the app's middle dot and says
 * nothing about the ones that do not. Never invents a fact: an empty line is left empty and the
 * view draws no sentence at all.
 */
export function connectionFacts(card) {
  const head = (card?.offers || [])[0] || {}
  const facts = card?.facts || head.facts || {}
  const parts = []
  // The SENDER, and only for mail: an entity offer has no one it is "from", and the card's `name`
  // is then its title — which the review card's header already shows (Pouria, 2026-10-01).
  const from = String(card?.sender || head.from || '').trim()
  if (from) parts.push(from)
  const people = (facts.participants || []).filter(Boolean)
  if (!from && people.length) parts.push(people.join(', '))
  const count = (card?.offers || []).length
  if (count > 1) parts.push(`${count} emails`)
  const home = String(facts.homeOngoingTitle || '').trim()
  if (home) parts.push(`In ${home}`)
  const due = String(facts.dueDate || '').trim()
  if (due) parts.push(`Due ${connectionDate(due) || due}`)
  // When an entity was MADE, where the server says (busy-timeline-srv#42's `facts.createdUtc`) —
  // "Created Sep 7" is the frame's reason written as a fact ("Created while researching …").
  const created = connectionDate(facts.createdUtc)
  if (created) parts.push(`Created ${created}`)
  // …in the table's own "Sep 7" (Pouria, 2026-10-01), so the review card and the row above it
  // write the same date the same way.
  // A date the "Created" part has already said is not said twice ("Created Sep 7 · Sep 7").
  const updated = connectionDate(card?.received) || String(card?.updated || '').trim()
  if (updated && updated !== created) parts.push(updated)
  return parts.join(' · ')
}

/** The receipt a refusal leaves (busy-my-web#495 (d)) — short, and in the notice's own voice. */
export function rejectedNotice(name, module = 'email') {
  const label = String(name || '').trim()
  const kind = MODULE_NOUN[connectionModule({ module })] || MODULE_NOUN.email
  const said = `${kind.toLowerCase()} marked not related`
  return label ? `${label} · ${said.charAt(0).toUpperCase()}${said.slice(1)}` : said.charAt(0).toUpperCase() + said.slice(1)
}

export function connectedNotice(name, module = 'email') {
  const label = String(name || '').trim()
  const kind = MODULE_NOUN[connectionModule({ module })] || MODULE_NOUN.email
  const said = `${kind} connected to this ongoing`
  return label ? `${label} · ${said}` : said.charAt(0).toUpperCase() + said.slice(1)
}

/** What each module is CALLED in the notice — the nouns the rest of the app uses. */
const MODULE_NOUN = {
  email: 'Email',
  task: 'Task',
  note: 'Note',
  event: 'Event',
  contact: 'Contact',
  research: 'Research pack',
  document: 'Document',
  messaging: 'Message',
}

export default connectionRows
